import { useMutation } from "@apollo/client";
import {
  // Legacy imports for backward compatibility
  MARK_NOTIFICATIONS_AS_READ,
  MARK_USER_NOTIFICATIONS_AS_READ,
} from "@/graphql/notifications";
import { useAsyncAction } from "@/lib/hooks/use-async-action";
import { useAuthStore } from "@/lib/store/auth-enhanced";
import { useSalonStore } from "@/lib/store/salon";

export const useMarkUserNotificationsAsRead = () => {
  const [markAsReadMutation] = useMutation(MARK_USER_NOTIFICATIONS_AS_READ);
  const { user } = useAuthStore();
  const { currentSalon } = useSalonStore();

  const userId = user?.id || null;
  const salonId = currentSalon?.id || null;

  return useAsyncAction(
    async (notificationIds: string[], markAll = false) => {
      const result = await markAsReadMutation({
        variables: {
          input: {
            notificationIds: markAll ? [] : notificationIds,
            markAllAsRead: markAll,
            salonId: salonId,
            userId: userId,
          },
        },
        optimisticResponse: {
          markNotificationsAsRead: true,
        },
        update: (cache, { data }) => {
          if (data?.markNotificationsAsRead) {
            // Update the cache to mark notifications as read
            cache.evict({ fieldName: "getNotifications" });
            cache.evict({ fieldName: "getNotificationStats" });
            cache.gc();
          }
        },
      });
      return result.data?.markNotificationsAsRead;
    },
    {
      loadingMessage: "Marking notifications as read...",
      successMessage: "Notifications marked as read",
      errorMessage: "Failed to mark notifications as read",
    }
  );
};

// Legacy hook for backward compatibility
export const useMarkNotificationsAsRead = () => {
  const [markAsReadMutation] = useMutation(MARK_NOTIFICATIONS_AS_READ);
  const { user } = useAuthStore();
  const { currentSalon } = useSalonStore();

  const recipientId = user?.id || null;
  const salonId = currentSalon?.id || null;

  return useAsyncAction(
    async (notificationIds: string[], markAll = false) => {
      const result = await markAsReadMutation({
        variables: {
          input: {
            notificationIds: markAll ? [] : notificationIds,
            markAllAsRead: markAll,
            salonId: salonId,
            userId: recipientId,
          },
        },
        optimisticResponse: {
          markNotificationsAsRead: true,
        },
        update: (cache, { data }) => {
          if (data?.markNotificationsAsRead) {
            // Update the cache to mark notifications as read
            cache.evict({ fieldName: "getNotifications" });
            cache.evict({ fieldName: "getNotificationStats" });
            cache.gc();
          }
        },
      });
      return result.data?.markNotificationsAsRead;
    },
    {
      loadingMessage: "Marking notifications as read...",
      successMessage: "Notifications marked as read",
      errorMessage: "Failed to mark notifications as read",
    }
  );
};

// Combined mutation hook for easier use in main component (preferred)
export function useUserNotificationMutation() {
  const { execute: markAsRead, isLoading: markingAsRead } = useMarkUserNotificationsAsRead();

  return {
    markAsRead,
    deleteNotifications: async () => {
      console.warn("deleteNotifications is not implemented");
      return false;
    },
    loading: markingAsRead,
  };
}

// Legacy hook for backward compatibility
export function useNotificationMutation() {
  const { execute: markAsRead, isLoading: markingAsRead } = useMarkNotificationsAsRead();

  return {
    markAsRead,
    deleteNotifications: async () => {
      console.warn("deleteNotifications is not implemented");
      return false;
    },
    loading: markingAsRead,
  };
}
